import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Grid, Card, CardContent, Typography, Tabs, Tab, LinearProgress, Table, TableHead, TableRow, TableCell, TableBody, Chip, CircularProgress, Tooltip, IconButton } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Person, Assessment } from '@mui/icons-material';
import StudentWallNavbar from '../../components/StudentWallNavbar';
import axiosInstance from '../../utils/axiosInstance';
import config from '../../config.json';
import io from 'socket.io-client';
import url from '../../url.json';
import { sendAnalyticsEvent } from '../../utils/analytics';
import ApexCharts from 'apexcharts';

// 工具名稱對應
const toolNameMap = {
  'idea-wall': '想法牆',
  'question-wall': '問題牆',
  'cross-group': '跨組討論',
  'dashboard': '儀表板'
};

// 計算工具提示
function SectionTitle({ children, tooltip }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
      <Typography variant="h6" sx={{ fontWeight: 600 }}>{children}</Typography>
      {tooltip && (
        <Tooltip title={tooltip} arrow>
          <IconButton size="small" sx={{ ml: 0.5, p: 0.5 }}>
            <InfoOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
}

export default function StudentAnalyticsDashboard() {
  const { courseDispatchId, groupId } = useParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState(0); // 0 課中｜1 課後
  const [loading, setLoading] = useState(true);
  
  // 課中即時資料
  const [realtimeData, setRealtimeData] = useState({
    interactionsThisClass: 0,
    idleMinutes: 0,
    tips: []
  });
  
  // 課後回顧資料
  const [postClassData, setPostClassData] = useState({
    weeklyTrend: [],
    tasks: [],
    suggestedContent: []
  });
  const [postLastUpdatedAt, setPostLastUpdatedAt] = useState(null);
  
  // 圖表資料
  const [chartNodes, setChartNodes] = useState([]);

  // WebSocket 追蹤
  const [ws, setWs] = useState(null);
  const lastInteractionTimeRef = useRef(null);
  const interactionCountRef = useRef(0);
  const actorIdRef = useRef(null);

  // Tool Use 事件（學生進入/離開儀表板）
  useEffect(() => {
    const actorId = sessionStorage.getItem('userId') || localStorage.getItem('userId');
    if (!courseDispatchId || !actorId) return;
    
    // 發送開始事件
    sendAnalyticsEvent({ courseDispatchId, type: 'ToolUseStarted', tool: 'dashboard', actorId });
    
    // 發送結束事件的函數
    const sendEndEvent = (useBeacon = false) => {
      sendAnalyticsEvent({ courseDispatchId, type: 'ToolUseEnded', tool: 'dashboard', actorId, useBeacon });
    };
    
    // 處理頁面關閉或切換（使用 keepalive 確保事件發送）
    const handleBeforeUnload = () => {
      sendEndEvent(true); // 使用 keepalive
    };
    
    // 處理頁面隱藏（切換到其他標籤或最小化）
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // 頁面隱藏時發送結束事件
        sendEndEvent(false);
      } else {
        // 頁面重新顯示時發送開始事件
        sendAnalyticsEvent({ courseDispatchId, type: 'ToolUseStarted', tool: 'dashboard', actorId });
      }
    };
    
    // 處理頁面卸載（pagehide 比 beforeunload 更可靠）
    const handlePageHide = () => {
      sendEndEvent(true); // 使用 keepalive
    };
    
    // 註冊事件監聽器
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('pagehide', handlePageHide);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // cleanup：正常離開頁面時發送結束事件
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('pagehide', handlePageHide);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      sendEndEvent(false);
    };
  }, [courseDispatchId]);

  // 獲取真實資料
  useEffect(() => {
    if (!courseDispatchId) return;
    let mounted = true;
    
    const actorId = sessionStorage.getItem('userId') || localStorage.getItem('userId');
    if (!actorId) {
      console.error('[StudentAnalytics] 無法取得學生ID');
      return;
    }
    actorIdRef.current = String(actorId);

    const fetchData = async () => {
      try {
        setLoading(true);
        
        // 獲取 Caliper 事件（學生的）
        const [eventsResp, tasksResp, nodesResp] = await Promise.allSettled([
          axiosInstance.get(`api/analytics/events/${courseDispatchId}`),
          axiosInstance.get(config.getStudentTasks),
          axiosInstance.get(`${config.getWallNodes}${courseDispatchId}/idea/${groupId || ''}`)
        ]);

        const events = (eventsResp.status === 'fulfilled' && eventsResp.value.data?.success)
          ? (eventsResp.value.data.data || [])
          : [];
        
        // 過濾出當前學生的事件
        const studentEvents = events.filter(e => String(e.actor_id) === actorIdRef.current);
        console.log('[StudentAnalytics] 學生事件數量:', studentEvents.length);

        // 課中即時：本節互動次數、連續未互動時長、目前使用的工具
        const sessionStartTime = new Date();
        sessionStartTime.setHours(0, 0, 0, 0); // 今天的開始時間
        const todayEvents = studentEvents.filter(e => {
          const eventTime = new Date(e.created_at || e.metadata?.original_timestamp || 0);
          return eventTime >= sessionStartTime;
        });

        // 計算互動次數（ForumPosted, ForumReplied, EdgeCreated, ReadingStarted 等）
        const interactionEvents = todayEvents.filter(e => 
          ['ForumPosted', 'ForumReplied', 'EdgeCreated', 'ReadingStarted'].includes(e.event_type)
        );
        const interactionsThisClass = interactionEvents.length;

        // 計算連續未互動時長（從最後一個互動事件到現在）
        let idleMinutes = 0;
        if (interactionEvents.length > 0) {
          const lastInteraction = interactionEvents[interactionEvents.length - 1];
          const lastInteractionTime = new Date(lastInteraction.created_at || lastInteraction.metadata?.original_timestamp || 0);
          const now = new Date();
          idleMinutes = Math.floor((now - lastInteractionTime) / (1000 * 60));
        } else if (todayEvents.length > 0) {
          // 如果沒有互動事件，從最後一個事件計算
          const lastEvent = todayEvents[todayEvents.length - 1];
          const lastEventTime = new Date(lastEvent.created_at || lastEvent.metadata?.original_timestamp || 0);
          const now = new Date();
          idleMinutes = Math.floor((now - lastEventTime) / (1000 * 60));
        }
        lastInteractionTimeRef.current = idleMinutes === 0 ? new Date() : null;
        interactionCountRef.current = interactionsThisClass;

        // 生成建議
        const tips = [];
        if (interactionsThisClass === 0) {
          tips.push('開始參與討論，發表你的想法');
          tips.push('開啟想法牆或問題牆開始學習');
        } else if (interactionsThisClass < 3) {
          tips.push('嘗試回應同組同學的便利貼');
          tips.push('將想法與他人連線');
        } else if (idleMinutes > 5) {
          tips.push('你已經一段時間沒有互動了，重新參與討論');
        }

        // 課後：本週互動趨勢
        const oneWeekAgo = new Date();
        oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
        const weekEvents = studentEvents.filter(e => {
          const eventTime = new Date(e.created_at || e.metadata?.original_timestamp || 0);
          return eventTime >= oneWeekAgo;
        });

        // 按日期分組統計互動次數
        const dailyStats = {};
        weekEvents
          .filter(e => ['ForumPosted', 'ForumReplied', 'EdgeCreated', 'ReadingStarted'].includes(e.event_type))
          .forEach(e => {
            const eventTime = new Date(e.created_at || e.metadata?.original_timestamp || 0);
            const dayKey = eventTime.toLocaleDateString('zh-TW', { weekday: 'short' });
            dailyStats[dayKey] = (dailyStats[dayKey] || 0) + 1;
          });
        
        const weekDays = ['週一', '週二', '週三', '週四', '週五', '週六', '週日'];
        const weeklyTrend = weekDays.map(day => ({
          day: day.replace('週', ''),
          count: dailyStats[day] || 0
        }));

        // 任務回顧
        let tasks = [];
        if (tasksResp.status === 'fulfilled' && tasksResp.value.data?.success) {
          const rawTasks = tasksResp.value.data.data || [];
          tasks = rawTasks
            .filter(t => String(t.course_dispatch_id || t.courseDispatchId) === String(courseDispatchId))
            .map(t => {
              // 計算任務花費時間（從 AssignableStarted/Completed 事件）
              const taskEvents = studentEvents.filter(e => 
                e.object_type === 'ApplicationTask' && 
                String(e.object_id) === String(t.id || t.task_id)
              );
              let spent = 0;
              const started = taskEvents.find(e => e.event_type === 'AssignableStarted');
              const completed = taskEvents.find(e => e.event_type === 'AssignableCompleted');
              if (started && completed) {
                const startTime = new Date(started.created_at || started.metadata?.original_timestamp || 0);
                const endTime = new Date(completed.created_at || completed.metadata?.original_timestamp || 0);
                spent = Math.floor((endTime - startTime) / (1000 * 60)); // 分鐘
              } else if (started) {
                // 如果開始但未完成，計算到現在的時間
                const startTime = new Date(started.created_at || started.metadata?.original_timestamp || 0);
                const now = new Date();
                spent = Math.floor((now - startTime) / (1000 * 60));
              }

              return {
                id: t.id || t.task_id,
                title: t.title || t.task_title || '未命名任務',
                status: t.status === 'completed' || t.status === '已完成' ? '已完成' : '未完成',
                spent: spent || 0
              };
            });
        }

        // 建議補看內容（未讀的重要節點）
        let suggestedContent = [];
        let allNodes = [];
        if (nodesResp.status === 'fulfilled' && nodesResp.value.data?.success) {
          const nodes = nodesResp.value.data.data || [];
          allNodes = nodes;
          
          // 找出已讀的節點（ReadingStarted 事件）
          const readNodeIds = new Set(
            studentEvents
              .filter(e => e.event_type === 'ReadingStarted' && e.object_type === 'WallNode')
              .map(e => String(e.object_id))
          );

          // 找出未讀且重要的節點（非回覆，且沒有閱讀記錄）
          const unreadNodes = nodes.filter(n => 
            n.node_type !== 'reply' &&
            !readNodeIds.has(String(n.id))
          );

          suggestedContent = unreadNodes.slice(0, 5).map(n => ({
            id: n.id,
            title: n.title || (n.content || '').replace(/<[^>]*>/g, '').substring(0, 30) || '未命名',
            reason: '重要內容、你尚未閱讀'
          }));
        }

        if (mounted) {
          setRealtimeData({
            interactionsThisClass,
            idleMinutes,
            tips
          });
          setPostClassData({
            weeklyTrend,
            tasks,
            suggestedContent
          });
          setChartNodes(allNodes);
          setPostLastUpdatedAt(new Date());
        }
      } catch (err) {
        console.error('[StudentAnalytics] 載入資料失敗:', err);
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    fetchData();
    return () => { mounted = false; };
  }, [courseDispatchId, groupId]);

  // WebSocket: 監聽 analytics 事件，更新即時互動
  useEffect(() => {
    if (!courseDispatchId || !actorIdRef.current) return;
    const socket = io(url.socketioHost, { path: '/s/socket.io' });
    setWs(socket);
    
    const channel = `analytics-event-${courseDispatchId}`;
    const handler = (evt) => {
      if (!evt || String(evt.course_dispatch_id) !== String(courseDispatchId)) return;
      if (String(evt.actor_id) !== actorIdRef.current) return; // 只處理自己的事件

      const tool = evt.tool;
      const eventType = evt.type;

      // 更新互動次數
      if (['ForumPosted', 'ForumReplied', 'EdgeCreated', 'ReadingStarted'].includes(eventType)) {
        interactionCountRef.current += 1;
        lastInteractionTimeRef.current = new Date();
        
        setRealtimeData(prev => ({
          ...prev,
          interactionsThisClass: interactionCountRef.current,
          idleMinutes: 0
        }));
      }
    };

    socket.on(channel, handler);
    return () => {
      socket.off(channel, handler);
      socket.close();
    };
  }, [courseDispatchId]);

  // 定期更新未互動時長
  useEffect(() => {
    if (!lastInteractionTimeRef.current) return;
    const interval = setInterval(() => {
      if (lastInteractionTimeRef.current) {
        const now = new Date();
        const idleMinutes = Math.floor((now - lastInteractionTimeRef.current) / (1000 * 60));
        setRealtimeData(prev => ({
          ...prev,
          idleMinutes
        }));
      }
    }, 60000); // 每分鐘更新一次

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="student-layout student-analytics-dashboard-container">
      <div className="main-content-v2">
        <StudentWallNavbar 
          courseDispatchId={courseDispatchId} 
          groupId={groupId}
          currentWall="analytics"
          onWallChange={(wall) => {
            const activeGroupId = groupId || sessionStorage.getItem('groupId') || '1';
            if (wall === 'idea') {
              navigate(`/student/idea-wall/${courseDispatchId}/${activeGroupId}`);
            } else if (wall === 'question') {
              navigate(`/student/question-wall/${courseDispatchId}/${activeGroupId}`);
            } else if (wall === 'cross_group') {
              navigate(`/student/cross-group/${courseDispatchId}`);
            }
          }}
        />

        <div className="class-header-row">
          <h1 className="main-title">我的學習儀表板</h1>
        </div>

        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)}>
            <Tab icon={<Person />} iconPosition="start" label="課中（即時）" />
            <Tab icon={<Assessment />} iconPosition="start" label="課後（回顧）" />
          </Tabs>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {tab === 0 && <RealtimeView data={realtimeData} nodes={chartNodes} />}
            {tab === 1 && <PostClassView data={postClassData} lastUpdatedAt={postLastUpdatedAt} />}
          </>
        )}
      </div>
    </div>
  );
}

// 圖表工具函數
const clearCharts = () => {
  const chartIds = ['tag-chart', 'author-tag-chart', 'cumulative-chart', 'radar-chart'];
  chartIds.forEach(id => {
    try {
      ApexCharts.exec(id, "destroy");
    } catch (e) {
      // 忽略錯誤
    }
  });
};

const renderChart = (selector, options) => {
  const chartElement = document.querySelector(selector);
  if (chartElement) {
    try {
      ApexCharts.exec(selector.replace('#', ''), "destroy");
    } catch (e) {
      // 忽略錯誤
    }
    const chart = new ApexCharts(chartElement, options);
    chart.render();
  } else {
    console.warn(`${selector} element not found`);
  }
};

const nodeTypeMapping = {
  'idea': '想法',
  'information': '資訊',
  'question': '提問',
  'experiment': '實驗',
  'record': '紀錄',
  'reply': '回覆'
};

const TagsCounter = (nodes) => {
  const counter = {
    'idea': 0,
    'information': 0,
    'question': 0,
    'experiment': 0,
    'record': 0,
    'reply': 0,
  };
  nodes.forEach(node => {
    const nodeType = node.node_type || node.nodeType || 'idea';
    if (counter.hasOwnProperty(nodeType)) {
      counter[nodeType]++;
    }
  });
  return [counter.idea, counter.information, counter.question, counter.experiment, counter.record, counter.reply];
};

const prepareData = (nodes) => {
  const sortedNodes = [...nodes].sort((a, b) => {
    const dateA = new Date(a.updated_at || a.updatedAt || a.created_at || a.createdAt || 0);
    const dateB = new Date(b.updated_at || b.updatedAt || b.created_at || b.createdAt || 0);
    return dateA - dateB;
  });

  const authorCumulativeCount = {};
  const seriesData = {};

  sortedNodes.forEach(node => {
    const author = node.author_name || node.Author?.name || node.author || '未知';
    const updatedAt = node.updated_at || node.updatedAt || node.created_at || node.createdAt || new Date().toISOString();
    
    if (!authorCumulativeCount[author]) {
      authorCumulativeCount[author] = 0;
      seriesData[author] = [];
    }
    authorCumulativeCount[author]++;
    seriesData[author].push({
      x: new Date(updatedAt),
      y: authorCumulativeCount[author]
    });
  });

  return seriesData;
};

const prepareSeries = (seriesData) => {
  return Object.keys(seriesData).map(author => ({
    name: author,
    data: seriesData[author]
  }));
};

const countKeywords = (nodes) => {
  const keywords = ["我的想法", "我覺得更好的想法", "我想知道", "這個想法不能解釋", "舉例和參考來源", "我的總結"];
  const authorKeywordCount = {};

  nodes.forEach(node => {
    const author = node.author_name || node.Author?.name || node.author || '未知';
    const content = node.content || '';
    
    if (content && typeof content === 'string') {
      if (!authorKeywordCount[author]) {
        authorKeywordCount[author] = {
          "我的想法": 0,
          "我覺得更好的想法": 0,
          "我想知道": 0,
          "這個想法不能解釋": 0,
          "舉例和參考來源": 0,
          "我的總結": 0
        };
      }
      keywords.forEach(keyword => {
        if (content.includes(keyword)) {
          authorKeywordCount[author][keyword]++;
        }
      });
    }
  });

  return authorKeywordCount;
};

const renderTagChart = (nodes) => {
  const tagsCounter = TagsCounter(nodes);
  const options = {
    chart: {
      id: 'tag-chart',
      type: 'bar',
      colors: ['#FFFFCC', '#CCFFCC', '#CCCCFF', '#FFDBDB', '#D0F4FF', '#FFFFFF'],
    },
    series: [{
      name: '標籤數量',
      data: tagsCounter,
    }],
    xaxis: {
      categories: ['想法', '資訊', '提問', '實驗', '紀錄', '回覆'],
    },
    title: {
      text: '累計標籤圖',
      align: 'center',
      margin: 10,
      style: {
        fontSize: '16px',
        color: '#333'
      }
    }
  };
  renderChart("#tag-chart", options);
};

const renderAuthorTagChart = (nodes) => {
  const authorTagCount = nodes.reduce((acc, node) => {
    const author = node.author_name || node.Author?.name || node.author || '未知';
    const nodeType = node.node_type || node.nodeType || 'idea';
    
    if (!acc[author]) {
      acc[author] = {
        'idea': 0,
        'information': 0,
        'question': 0,
        'experiment': 0,
        'record': 0,
        'reply': 0,
      };
    }
    if (acc[author].hasOwnProperty(nodeType)) {
      acc[author][nodeType]++;
    }
    return acc;
  }, {});

  const authors = Object.keys(authorTagCount);
  const dataSeries = Object.keys(nodeTypeMapping).map(type => ({
    name: nodeTypeMapping[type],
    data: authors.map(author => authorTagCount[author][type] || 0),
  }));

  const options = {
    chart: {
      id: 'author-tag-chart',
      type: 'bar',
    },
    series: dataSeries,
    xaxis: {
      categories: authors,
    },
    title: {
      text: '個人標籤圖',
      align: 'center',
      margin: 10,
      style: {
        fontSize: '16px',
        color: '#333'
      }
    }
  };

  renderChart("#author-tag-chart", options);
};

const renderRadarChart = (authorKeywordCount) => {
  const categories = ["我的想法", "我覺得更好的想法", "我想知道", "這個想法不能解釋", "舉例和參考來源", "我的總結"];
  const seriesData = Object.keys(authorKeywordCount).map(author => ({
    name: author,
    data: categories.map(keyword => Math.ceil(authorKeywordCount[author][keyword] || 0)),
  }));

  const options = {
    chart: {
      id: 'radar-chart',
      type: 'radar',
    },
    series: seriesData,
    xaxis: {
      categories: categories,
    },
    yaxis: {
      labels: {
        formatter: value => Math.round(value).toString(),
      },
      min: 0,
      tickAmount: 4,
    },
    dataLabels: {
      enabled: false,
    },
    title: {
      text: '鷹架數量圖',
      align: 'center',
      margin: 10,
      style: {
        fontSize: '16px',
        color: '#333'
      }
    }
  };

  renderChart("#radar-chart", options);
};

const renderCumulativeChart = (series) => {
  const options = {
    chart: {
      id: 'cumulative-chart',
      type: 'line'
    },
    series: series,
    xaxis: {
      type: 'datetime',
      title: {
        text: '時間'
      }
    },
    yaxis: {
      title: {
        text: '累計節點數'
      }
    },
    title: {
      text: '累計節點時間圖',
      align: 'center',
      margin: 10,
      style: {
        fontSize: '16px',
        color: '#333'
      }
    }
  };

  renderChart("#cumulative-chart", options);
};

const renderCharts = (nodes) => {
  if (!nodes || nodes.length === 0) return;
  clearCharts();
  
  setTimeout(() => {
    renderTagChart(nodes);
    renderAuthorTagChart(nodes);
    renderCumulativeChart(prepareSeries(prepareData(nodes)));
    const authorKeywordCount = countKeywords(nodes);
    if (Object.keys(authorKeywordCount).length > 0) {
      renderRadarChart(authorKeywordCount);
    }
  }, 100);
};

function RealtimeView({ data, nodes }) {
  const d = data || { interactionsThisClass: 0, idleMinutes: 0, tips: [] };
  
  // 渲染圖表
  useEffect(() => {
    if (nodes && nodes.length > 0) {
      renderCharts(nodes);
    }
    return () => {
      clearCharts();
    };
  }, [nodes]);
  
  return (
    <Grid container spacing={3}>
      {/* 互動統計 */}
      <Grid item xs={12} sm={6}>
        <Card sx={{ height: '100%' }}>
          <CardContent>
            <SectionTitle tooltip="本節課你的互動次數（包含發表、回覆、連線、閱讀）">本節互動</SectionTitle>
            <Typography variant="h3" color="primary.main" sx={{ mb: 1 }}>{d.interactionsThisClass}</Typography>
            <Typography variant="body2" color="text.secondary">次互動</Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} sm={6}>
        <Card sx={{ height: '100%' }}>
          <CardContent>
            <SectionTitle tooltip="距離你最後一次互動的時間">未互動時長</SectionTitle>
            <Typography variant="h3" color="warning.main" sx={{ mb: 1 }}>{d.idleMinutes}</Typography>
            <Typography variant="body2" color="text.secondary">分鐘</Typography>
          </CardContent>
        </Card>
      </Grid>

      {/* 圖表視覺化 */}
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <SectionTitle tooltip="顯示你小組的互動趨勢與標籤分布">小組互動圖表</SectionTitle>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <div id="cumulative-chart" style={{ height: 300 }} />
              </Grid>
              <Grid item xs={12} md={6}>
                <div id="radar-chart" style={{ height: 300 }} />
              </Grid>
              <Grid item xs={12} md={6}>
                <div id="tag-chart" style={{ height: 300 }} />
              </Grid>
              <Grid item xs={12} md={6}>
                <div id="author-tag-chart" style={{ height: 300 }} />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      {d.tips && d.tips.length > 0 && (
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <SectionTitle tooltip="根據你的學習狀態提供的建議">建議</SectionTitle>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {d.tips.map((t, i) => (
                  <Chip key={i} label={t} color="success" size="small" />
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      )}
    </Grid>
  );
}

function PostClassView({ data, lastUpdatedAt }) {
  const d = data || { weeklyTrend: [], tasks: [], suggestedContent: [] };
  const maxCount = d.weeklyTrend.length > 0 
    ? Math.max(...d.weeklyTrend.map(t => t.count), 1)
    : 1;
  
  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <Card sx={{ height: '100%' }}>
          <CardContent>
            <SectionTitle tooltip="過去一週你的互動次數趨勢">本週互動趨勢</SectionTitle>
            {lastUpdatedAt && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                最後更新：{lastUpdatedAt.toLocaleTimeString('zh-TW')}
              </Typography>
            )}
            {d.weeklyTrend.length > 0 ? (
              d.weeklyTrend.map((p) => (
                <Box key={p.day} sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2">{p.day}</Typography>
                    <Typography variant="caption" color="text.secondary">{p.count} 次</Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={Math.min((p.count / maxCount) * 100, 100)} 
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                </Box>
              ))
            ) : (
              <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 3 }}>
                本週尚無互動記錄
              </Typography>
            )}
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={6}>
        <Card sx={{ height: '100%' }}>
          <CardContent>
            <SectionTitle tooltip="你參與的任務及其花費時間">任務回顧</SectionTitle>
            {lastUpdatedAt && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                最後更新：{lastUpdatedAt.toLocaleTimeString('zh-TW')}
              </Typography>
            )}
            {d.tasks && d.tasks.length > 0 ? (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>任務</TableCell>
                    <TableCell>狀態</TableCell>
                    <TableCell align="right">花費(分)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {d.tasks.map(t => (
                    <TableRow key={t.id}>
                      <TableCell>{t.title}</TableCell>
                      <TableCell>
                        <Chip 
                          size="small" 
                          color={t.status === '已完成' ? 'success' : 'warning'} 
                          label={t.status} 
                        />
                      </TableCell>
                      <TableCell align="right">{t.spent || 0}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 3 }}>
                尚無任務記錄
              </Typography>
            )}
          </CardContent>
        </Card>
      </Grid>

      {d.suggestedContent && d.suggestedContent.length > 0 && (
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <SectionTitle tooltip="根據你的閱讀記錄推薦的重要內容">建議補看內容</SectionTitle>
              {lastUpdatedAt && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                  最後更新：{lastUpdatedAt.toLocaleTimeString('zh-TW')}
                </Typography>
              )}
              {d.suggestedContent.map(c => (
                <Box key={c.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, p: 1.5, bgcolor: 'info.light', borderRadius: 1 }}>
                  <Typography variant="body2">{c.title}</Typography>
                  <Chip size="small" color="info" label={c.reason} />
                </Box>
              ))}
            </CardContent>
          </Card>
        </Grid>
      )}
    </Grid>
  );
}

